import React, { useState } from 'react';
import { useI18n } from '../../../contexts/I18nContext';
import './JsonPanel.css';

interface Props {
  title: string;
  /** What to display; null while nothing is loaded. */
  data: unknown | null;
  loading?: boolean;
  loadingLabel?: string;
  emptyLabel?: string;
  onRefresh: () => void;
  refreshTitle?: string;
  /** Extra controls rendered before the copy / refresh buttons. */
  children?: React.ReactNode;
}

/** A JSON payload with a title bar, copy and refresh — the Raw and V2/Conf panels. */
export const JsonPanel: React.FC<Props> = ({
  title, data, loading = false, loadingLabel, emptyLabel, onRefresh, refreshTitle, children,
}) => {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="modal-content tab-content">
      <div className="json-panel-header">
        <span className="json-panel-title">{title}</span>
        <div className="json-panel-actions">
          {children}
          {data != null && (
            <button
              type="button"
              className={`btn btn-sm btn-secondary json-panel-copy${copied ? ' copied' : ''}`}
              onClick={copy}
              title={t('devices.copyJsonToClipboard')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                {copied ? (
                  <polyline points="20 6 9 17 4 12" />
                ) : (
                  <>
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </>
                )}
              </svg>
              {copied ? 'Copied' : 'Copy'}
            </button>
          )}
          <button
            type="button"
            className="btn btn-sm btn-secondary json-panel-refresh"
            onClick={onRefresh}
            disabled={loading}
            title={refreshTitle}
          >
            <svg className={`refresh-icon${loading ? ' spinning' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="23 4 23 10 17 10" />
              <polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>
      <div className="raw-json-container">
        {loading && data == null ? (
          <div className="loading-indicator">{loadingLabel ?? 'Loading...'}</div>
        ) : data != null ? (
          <pre className="raw-json">{JSON.stringify(data, null, 2)}</pre>
        ) : (
          <div className="no-data">{emptyLabel ?? '-'}</div>
        )}
      </div>
    </div>
  );
};
